import React, { useEffect, useState } from 'react'
import { trans } from '../../../../_providers/Translation'
import AppliancesFieldDevice from './AppliancesFieldDevice'

// images
import deviceHeadImage from "@/assets/img/reservations/icons/device-head.svg"
import Api from '@/_helpers/Api'

export default function AppliancesField({
  task,
  setTask,
  allowSubscription = () => false,
  allowWarranty = () => false
}) {
  const [loadingAppliancesData, setLoadingAppliancesData] = useState(false)
  const [expandedIndex, setExpandedIndex] = useState(0)

  // Auto-expand the first appliance that has a validation error
  useEffect(() => {
    const validations = task?.validations || {}
    const hasError = key => validations[key]
    const firstErrorIdx = (task?.data?.appliances || []).findIndex((_, idx) =>
      Object.keys(validations).some(key => key.startsWith(`appliances.${idx}.`) && hasError(key))
    )
    if (firstErrorIdx !== -1) {
      setExpandedIndex(firstErrorIdx)
    }
  }, [JSON.stringify(task?.validations)])

  // helper: read appliances array from task
  const readAppliances = () => {
    if (task && task.data) return Array.isArray(task.data.appliances) ? task.data.appliances : []
    return []
  }

  // helper: write appliances array via setTask
  const writeAppliances = (nextArr) => {
    setTask(prev => {
      const next = { ...(prev || {}) }
      next.data = { ...(next.data || {}), appliances: nextArr }
      return next
    })
  }

  // ensure initial appliances
  useEffect(() => {
    const curr = Array.isArray(task.data?.appliances) ? task.data.appliances : []
    if (!curr.length) {
      writeAppliances([{}])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const appliances = readAppliances()
  const T = task || {}

  const updateApplianceField = (index, field, val) => {
    const next = appliances.slice()
    next[index] = { ...(next[index] || {}), [field]: val }
    writeAppliances(next)
  }

  const handleAdd = () => {
    const next = [...appliances, {}]
    writeAppliances(next)
    setExpandedIndex(next.length - 1)
  }

  const handleRemove = (index) => {
    const next = appliances.slice()
    next.splice(index, 1)
    writeAppliances(next)
  }

  const handleSelectSubscription = (e) => {
    const id = e.target.value
    const selected = (T.subscriptions || []).find(s => String(s.id) === String(id))

    setTask(prev => ({
      ...prev,
      data: {
        ...prev.data,
        subscription: selected || null,
        subscription_id: selected?.id || null,
        is_subscription: !!selected,
        warranty: null,
        warranty_id: null,
        is_warranty: false,
        appliancecategory: selected?.appliancecategory || {},
        appliancebrand: selected?.appliancebrand || {},
        appliancemodel: selected?.appliancemodel || {},
        serial_no: selected?.serial_no || null,
        product_code: selected?.product_code || null
      }
    }))

    if (id) {
      fetchSubscriptionDetails(id)
    }
  }

  const handleSelectWarranty = (e) => {
    const id = e.target.value
    const selected = (T.warranties || []).find(s => String(s.id) === String(id))

    setTask(prev => ({
      ...prev,
      data: {
        ...prev.data,
        warranty: selected || null,
        warranty_id: selected?.id || null,
        is_warranty: !!selected,
        subscription: null,
        subscription_id: null,
        is_subscription: false,
        appliancecategory: selected?.appliancecategory || {},
        appliancebrand: selected?.appliancebrand || {},
        appliancemodel: selected?.appliancemodel || {},
        serial_no: selected?.serial_no || null,
        product_code: selected?.product_code || null
      }
    }))

    if (id) {
      fetchWarrantyDetails(id)
    }
  }

  const handleSelectDevice = (index, e) => {
    const id = e.target.value
    const selected = (T.appliances?.categories || []).find(c => String(c.id) === String(id)) || {}
    const next = appliances.slice()
    next[index] = {
      ...(next[index] || {}),
      appliancecategory: { ...selected, id }
    }
    // reset brand/model
    next[index].appliancebrand = {}
    next[index].appliancemodel = {}
    writeAppliances(next)
  }

  const handleSelectBrand = (index, id, name) => {
    const next = appliances.slice()
    next[index] = {
      ...(next[index] || {}),
      appliancebrand: { id, name }
    }
    next[index].appliancemodel = {}
    writeAppliances(next)
  }

  const handleModelChange = (index, id, name) => {
    const next = appliances.slice()
    next[index] = {
      ...(next[index] || {}),
      appliancemodel: { id, name }
    }
    writeAppliances(next)
  }


  function fetchWarrantyDetails(id) {
    setLoadingAppliancesData(true)

    Api.post(`contractors/servicing/warranties/appliances?warranty_id=${id}`)
      .then((res) => {
        const appliancesData = [res.data]

        setTimeout(() => {
          setAppliances(appliancesData)
        }, 1000)
      })
      .finally(() => {
        setLoadingAppliancesData(false)
      })
  }

  function fetchSubscriptionDetails(id) {
    setLoadingAppliancesData(true)

    Api.post(`contractors/servicing/subscriptions/appliances?subscription_id=${id}`)
      .then((res) => {
        const appliancesData = res.data

        setTimeout(() => {
          setAppliances(appliancesData)
        }, 1000)
      })
      .finally(() => {
        setLoadingAppliancesData(false)
      })
  }

  function setAppliances(appliancesData) {
    if (Array.isArray(appliancesData) && appliancesData.length > 0) {
      // Transform API response to appliances array format
      const transformedAppliances = appliancesData.map((item) => {
        // Find matching objects from task data
        const category = (T.appliances?.categories || []).find(
          c => String(c.id) === String(item.appliance_category_id)
        )
        const brand = category?.brands?.find(
          b => String(b.id) === String(item.appliance_brand_id)
        )

        return {
          subscription_id: item.subscription_id,
          is_subscription: true,
          appliancecategory: category ? { id: item.appliance_category_id, name: category.name } : {},
          appliancebrand: brand ? { id: item.appliance_brand_id, name: brand.name } : { id: item.appliance_brand_id },
          appliancemodel: item.appliance_model_id ? { id: item.appliance_model_id } : {},
          serial_no: item.serial_no || null,
          product_code: item.product_code || null
        }
      })

      // Replace appliances array with transformed data
      writeAppliances(transformedAppliances)
    } else {
      // Reset appliances array if empty
      writeAppliances([{}])
    }
  }

  // disabled logic: read from task if present, otherwise allow editing
  const isChild = Boolean(T.data?.is_child)
  const canModify = (T.data && typeof T.data.can_modify !== 'undefined') ? Boolean(T.data.can_modify) : true
  const canModifyAppliance = (T.data && typeof T.data.can_modify_appliance !== 'undefined') ? Boolean(T.data.can_modify_appliance) : true
  return (
    <div className="appliances-field">
      <div
        className="head"
      >
        <img src={deviceHeadImage} alt="device" />
        {`${trans("random.itemToFix")} ${allowSubscription() ? trans('labels.bySubscription') : allowWarranty() ? trans('labels.byWarranty') : ''}`}
      </div>
      {allowSubscription() && (
        <div className="row">
          <div
            className="col"
            style={loadingAppliancesData ? {
              opacity: 0.4,
              pointerEvents: 'none',
            } : {}}
          >
            <label htmlFor="subscription_id">
              {trans("labels.subscription")}
            </label>
            <select
              name="subscription_id"
              id="subscription_id"
              value={
                task?.data?.subscription?.id
                  ? task.data.subscription.id
                  : ""
              }
              onChange={handleSelectSubscription}
              disabled={isChild || !canModify || !canModifyAppliance}
            >
              <option value="" disabled>{trans("random.select")}</option>
              {(T.subscriptions || []).map((s) => (
                <option key={"s-" + s.id} value={s.id}>
                  {`${s.name} (${s.ref})`}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {allowWarranty() && (
        <div className="row">
          <div
            className="col"
            style={loadingAppliancesData ? {
              opacity: 0.4,
              pointerEvents: 'none',
            } : {}}
          >
            <label htmlFor="warranty_id">
              {trans("labels.warranty")}
            </label>
            <select
              name="warranty_id"
              id="warranty_id"
              value={
                task?.data?.warranty?.id
                  ? task.data.warranty.id
                  : ""
              }
              onChange={handleSelectWarranty}
              disabled={isChild || !canModify || !canModifyAppliance}
            >
              <option value="" disabled>{trans("random.select")}</option>
              {(T.warranties || []).map((w) => (
                <option key={"w-" + w.id} value={w.id}>
                  {w.ref} (
                  {[
                    w?.appliancecategory?.name,
                    w?.appliancebrand?.name,
                    w?.appliancemodel?.name,
                  ]
                    .filter((e) => e)
                    .join(", ")}
                  )
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {(appliances.length ? appliances : [{}]).map((ap, idx) => (
        <AppliancesFieldDevice
          key={idx}
          appliance={ap}
          index={idx}
          totalCount={appliances.length}
          task={task}
          loading={T.loading || loadingAppliancesData}
          isChild={isChild}
          canModify={canModify}
          canModifyAppliance={canModifyAppliance}
          onUpdateField={updateApplianceField}
          onRemove={handleRemove}
          onSelectDevice={handleSelectDevice}
          onSelectBrand={handleSelectBrand}
          onModelChange={handleModelChange}
          isExpanded={appliances.length === 1 || expandedIndex === idx}
          onToggle={() => setExpandedIndex(prev => prev === idx ? null : idx)}
          hasError={Object.keys(task?.validations || {}).some(key => key.startsWith(`appliances.${idx}.`) && task.validations[key])}
          validations={task?.validations || {}}
        />
      ))}

      <div style={{ marginTop: 8 }}>
        <button type="button" className="button button-add button-small" onClick={handleAdd} disabled={isChild || !canModify}>
          {trans('buttons.newAppliance')}
        </button>
      </div>

    </div>
  )
}